import api from './api';
import { ApiResponse, Student, StudentRequest, PageResponse, StudentImportResult, StudentClassEnrollment, EnrollmentRequest } from '../types';

export const studentService = {
  // 获取学生列表
  getStudents: async (params: {
    schoolId: number;
    classId?: string;
    search?: string;
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<Student>> => {
    const response = await api.get<ApiResponse<PageResponse<Student>>>('/admin/students', {
      params: {
        schoolId: params.schoolId,
        classId: params.classId,
        search: params.search,
        page: params.page || 0,
        size: params.size || 20,
        sort: params.sort || 'name,asc',
      },
    });
    return response.data.data;
  },

  // 按身份证号查询学生
  getStudentByIdCard: async (idCardNumber: string): Promise<Student> => {
    const response = await api.get<ApiResponse<Student>>('/admin/students/by-id-card', {
      params: { idCardNumber },
    });
    return response.data.data;
  },

  // 获取学生详情
  getStudent: async (id: number): Promise<Student> => {
    const response = await api.get<ApiResponse<Student>>(`/admin/students/${id}`);
    return response.data.data;
  },

  // 创建学生
  createStudent: async (data: StudentRequest): Promise<Student> => {
    const response = await api.post<ApiResponse<Student>>('/admin/students', data);
    return response.data.data;
  },

  // 更新学生
  updateStudent: async (id: number, data: StudentRequest): Promise<Student> => {
    const response = await api.put<ApiResponse<Student>>(`/admin/students/${id}`, data);
    return response.data.data;
  },

  // 删除学生
  deleteStudent: async (id: number): Promise<void> => {
    await api.delete(`/admin/students/${id}`);
  },

  // 将学生从班级中移出（删除关联，不删除学生）
  removeFromClass: async (id: number, classId: string): Promise<void> => {
    await api.delete(`/admin/students/${id}/classes/${classId}`);
  },

  // 批量导入学生（Excel）
  importStudents: async (schoolId: number, file: File): Promise<StudentImportResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<ApiResponse<StudentImportResult>>(
      `/admin/students/import?schoolId=${schoolId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data.data;
  },

  // 重置学生密码（重置为身份证后6位）
  resetPassword: async (id: number): Promise<void> => {
    await api.post(`/admin/students/${id}/reset-password`);
  },

  // 修改学生密码
  changePassword: async (id: number, newPassword: string): Promise<void> => {
    await api.post(`/admin/students/${id}/change-password`, null, {
      params: { newPassword },
    });
  },

  // 获取学生就读历史
  getEnrollments: async (id: number): Promise<StudentClassEnrollment[]> => {
    const response = await api.get<ApiResponse<StudentClassEnrollment[]>>(`/admin/students/${id}/enrollments`);
    return response.data.data;
  },

  // 新增学生就读记录
  addEnrollment: async (id: number, data: EnrollmentRequest): Promise<StudentClassEnrollment> => {
    const response = await api.post<ApiResponse<StudentClassEnrollment>>(`/admin/students/${id}/enrollments`, data);
    return response.data.data;
  },

  // 下载导入模板（Excel）
  downloadImportTemplate: async (): Promise<void> => {
    const response = await api.get('/admin/students/import-template', {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'student_import_template.xlsx';
    link.click();
    window.URL.revokeObjectURL(url);
  },
};
